/* ==========================================================================
   ABS Developers — Simplified Careers Page
   File: assets/abs-careers.js
   ========================================================================== */

(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-abs-careers]';
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
  const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx'];
  const ALLOWED_MIME_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/octet-stream'
  ];

  function initAll(root = document) {
    root.querySelectorAll(ROOT_SELECTOR).forEach((section) => {
      if (section.dataset.absCareersInitialized === 'true') return;

      section.dataset.absCareersInitialized = 'true';
      initSection(section);
    });
  }

  function initSection(section) {
    const form = section.querySelector('[data-careers-form]');
    const revealItems = Array.from(section.querySelectorAll('[data-reveal]'));
    const successModal = section.querySelector('[data-success-modal]');
    const closeSuccessButtons = Array.from(
      section.querySelectorAll('[data-close-success]')
    );

    setupRevealAnimations(revealItems);
    setupFileInputs(section);
    setupFormSectionFocus(section);
    setupSuccessModal(successModal, closeSuccessButtons);

    if (form) {
      setupFormSubmission(section, form, successModal);
    }
  }

  /* ==========================================================================
     Reveal animations
     ========================================================================== */

  function setupRevealAnimations(elements) {
    if (!elements.length) return;

    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      elements.forEach((element) => {
        element.classList.add('is-visible');
      });

      return;
    }

    const observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add('is-visible');
          currentObserver.unobserve(entry.target);
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -5% 0px'
      }
    );

    elements.forEach((element, index) => {
      element.style.transitionDelay = `${Math.min(index * 80, 160)}ms`;
      observer.observe(element);
    });
  }

  /* ==========================================================================
     Friendly form section focus
     ========================================================================== */

  function setupFormSectionFocus(section) {
    const sections = Array.from(
      section.querySelectorAll('.abs-careers__form-section')
    );

    sections.forEach((formSection) => {
      const controls = Array.from(
        formSection.querySelectorAll('input, select, textarea')
      );

      controls.forEach((control) => {
        control.addEventListener('focus', () => {
          sections.forEach((item) => item.classList.remove('is-active'));
          formSection.classList.add('is-active');
        });
      });
    });
  }

  /* ==========================================================================
     Upload handling
     ========================================================================== */

  function setupFileInputs(section) {
    const fileInputs = Array.from(
      section.querySelectorAll('[data-file-input]')
    );

    fileInputs.forEach((input) => {
      const card = input.closest('[data-upload-card]');
      const fileName = card
        ? card.querySelector('[data-file-name]')
        : null;

      if (fileName && !fileName.dataset.defaultText) {
        fileName.dataset.defaultText = fileName.textContent.trim();
      }

      input.addEventListener('change', () => {
        input.setCustomValidity('');

        const file = input.files && input.files[0];

        if (!file) {
          resetUploadCard(card, fileName);
          return;
        }

        const validation = validateFile(file);

        if (!validation.valid) {
          input.value = '';
          input.setCustomValidity(validation.message);
          input.reportValidity();
          resetUploadCard(card, fileName);
          return;
        }

        if (card) {
          card.classList.add('is-selected');
        }

        if (fileName) {
          fileName.textContent = `${file.name} · ${formatBytes(file.size)}`;
        }
      });
    });
  }

  function validateFile(file) {
    const extension = getExtension(file.name);
    const mime = String(file.type || '').toLowerCase();

    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      return {
        valid: false,
        message: 'Please upload a PDF, DOC or DOCX file.'
      };
    }

    if (
      mime &&
      !ALLOWED_MIME_TYPES.includes(mime)
    ) {
      return {
        valid: false,
        message: 'Please upload a PDF, DOC or DOCX file.'
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        message: 'Maximum file size is 10 MB.'
      };
    }

    return {
      valid: true,
      message: ''
    };
  }

  function validateAllFiles(form) {
    let valid = true;

    form.querySelectorAll('[data-file-input]').forEach((input) => {
      input.setCustomValidity('');

      const file = input.files && input.files[0];

      if (!file) return;

      const result = validateFile(file);

      if (!result.valid) {
        valid = false;
        input.setCustomValidity(result.message);
      }
    });

    return valid;
  }

  function resetUploadCard(card, fileName) {
    if (card) {
      card.classList.remove('is-selected');
    }

    if (fileName) {
      fileName.textContent = fileName.dataset.defaultText || '';
    }
  }

  function resetAllUploadCards(form) {
    form.querySelectorAll('[data-upload-card]').forEach((card) => {
      const fileName = card.querySelector('[data-file-name]');
      resetUploadCard(card, fileName);
    });
  }

  function getExtension(fileName) {
    const pieces = String(fileName || '')
      .toLowerCase()
      .split('.');

    return pieces.length > 1 ? pieces.pop() : '';
  }

  function formatBytes(bytes) {
    if (!Number.isFinite(bytes) || bytes <= 0) {
      return '0 KB';
    }

    const megabytes = bytes / (1024 * 1024);

    if (megabytes >= 1) {
      return `${megabytes.toFixed(megabytes >= 10 ? 1 : 2)} MB`;
    }

    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  /* ==========================================================================
     Success modal
     ========================================================================== */

  function setupSuccessModal(modal, closeButtons) {
    if (!modal) return;

    closeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        closeSuccessModal(modal);
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hidden) {
        closeSuccessModal(modal);
      }
    });
  }

  function openSuccessModal(modal) {
    if (!modal) return;

    modal.hidden = false;
    document.documentElement.style.overflow = 'hidden';

    const closeButton = modal.querySelector('[data-close-success]');

    if (closeButton) {
      window.setTimeout(() => {
        closeButton.focus();
      }, 50);
    }
  }

  function closeSuccessModal(modal) {
    if (!modal) return;

    modal.hidden = true;
    document.documentElement.style.overflow = '';
  }

  /* ==========================================================================
     Form submission
     ========================================================================== */

  function setupFormSubmission(section, form, successModal) {
    const submitButton = form.querySelector('[data-submit-button]');
    const submitLabel = form.querySelector('[data-submit-label]');
    const messageBox = form.querySelector('[data-form-message]');

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      clearMessage(messageBox);
      clearCustomValidity(form);

      if (!validateAllFiles(form)) {
        form.reportValidity();
        focusFirstInvalid(form);
        return;
      }

      if (!form.reportValidity()) {
        focusFirstInvalid(form);
        return;
      }

      const honeypot = form.querySelector('input[name="website"]');

      if (honeypot && honeypot.value.trim() !== '') {
        return;
      }

      const endpoint = String(form.dataset.endpoint || '').trim();

      if (!endpoint) {
        showMessage(
          messageBox,
          'Google Apps Script is not connected yet. Add the Web App URL in the ABS Careers section settings.',
          'error'
        );

        return;
      }

      setLoading(submitButton, submitLabel, true);

      try {
        const payload = await buildPayload(form);

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'text/plain;charset=utf-8'
          },
          body: JSON.stringify(payload),
          redirect: 'follow'
        });

        const rawText = await response.text();

        let result = null;

        if (rawText) {
          try {
            result = JSON.parse(rawText);
          } catch (error) {
            result = null;
          }
        }

        if (!response.ok) {
          throw new Error(
            result && result.message
              ? result.message
              : 'Your application could not be submitted. Please try again.'
          );
        }

        if (result && result.success === false) {
          throw new Error(
            result.message ||
            'Your application could not be submitted. Please try again.'
          );
        }

        form.reset();
        resetAllUploadCards(form);
        clearMessage(messageBox);
        removeActiveSections(section);
        openSuccessModal(successModal);

      } catch (error) {
        console.error('[ABS Careers] Submission error:', error);

        showMessage(
          messageBox,
          error && error.message
            ? error.message
            : 'Something went wrong. Please try again.',
          'error'
        );
      } finally {
        setLoading(submitButton, submitLabel, false);
      }
    });
  }

  function clearCustomValidity(form) {
    form.querySelectorAll('input, select, textarea').forEach((field) => {
      if (typeof field.setCustomValidity === 'function') {
        field.setCustomValidity('');
      }
    });
  }

  function focusFirstInvalid(form) {
    const invalid = form.querySelector(':invalid');

    if (!invalid) return;

    const section = invalid.closest('.abs-careers__form-section');

    if (section) {
      scrollToFieldSection(section);
    }

    window.setTimeout(() => {
      try {
        invalid.focus({ preventScroll: true });
      } catch (error) {
        invalid.focus();
      }
    }, 300);
  }

  function scrollToFieldSection(section) {
    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    const headerOffset = getHeaderOffset();

    const y =
      section.getBoundingClientRect().top +
      window.scrollY -
      headerOffset;

    window.scrollTo({
      top: y,
      behavior: reduceMotion ? 'auto' : 'smooth'
    });
  }

  function getHeaderOffset() {
    const candidates = [
      document.querySelector('sticky-header'),
      document.querySelector('.header-wrapper'),
      document.querySelector('header.shopify-section')
    ].filter(Boolean);

    let height = 22;

    candidates.forEach((header) => {
      const style = window.getComputedStyle(header);

      if (
        style.position === 'fixed' ||
        style.position === 'sticky'
      ) {
        height = Math.max(
          height,
          header.getBoundingClientRect().height
        );
      }
    });

    return height + 20;
  }

  function removeActiveSections(section) {
    section
      .querySelectorAll('.abs-careers__form-section.is-active')
      .forEach((item) => {
        item.classList.remove('is-active');
      });
  }

  function setLoading(button, label, loading) {
    if (!button) return;

    button.disabled = loading;
    button.classList.toggle('is-loading', loading);

    if (label) {
      if (!label.dataset.defaultLabel) {
        label.dataset.defaultLabel =
          label.textContent.trim() || 'Apply Now';
      }

      label.textContent = loading
        ? 'Sending Application'
        : label.dataset.defaultLabel;
    }
  }

  function showMessage(box, text, type = 'error') {
    if (!box) return;

    box.textContent = text;
    box.classList.remove('is-error', 'is-success');
    box.classList.add(
      'is-visible',
      type === 'success' ? 'is-success' : 'is-error'
    );

    box.scrollIntoView({
      behavior: window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches
        ? 'auto'
        : 'smooth',
      block: 'nearest'
    });
  }

  function clearMessage(box) {
    if (!box) return;

    box.textContent = '';
    box.classList.remove(
      'is-visible',
      'is-error',
      'is-success'
    );
  }

  /* ==========================================================================
     Prepare Google Apps Script payload
     ========================================================================== */

  async function buildPayload(form) {
    const formData = new FormData(form);

    const payload = {
      submitted_at: new Date().toISOString(),
      form_fields: {},
      files: {}
    };

    for (const [name, value] of formData.entries()) {
      if (value instanceof File) {
        if (!value.name) continue;

        payload.files[name] = await fileToPayload(value);
      } else {
        payload.form_fields[name] = String(value);
      }
    }

    return payload;
  }

  function fileToPayload(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const dataUrl = String(reader.result || '');
        const commaPosition = dataUrl.indexOf(',');

        resolve({
          name: file.name,
          type: file.type || 'application/octet-stream',
          size: file.size,
          base64:
            commaPosition >= 0
              ? dataUrl.slice(commaPosition + 1)
              : dataUrl
        });
      };

      reader.onerror = () => {
        reject(
          new Error(
            `Could not read ${file.name}. Please select the file again.`
          )
        );
      };

      reader.readAsDataURL(file);
    });
  }

  /* ==========================================================================
     Initialize
     ========================================================================== */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAll();
    });
  } else {
    initAll();
  }

  // Reinitialize when Shopify reloads a section in the theme editor.
  document.addEventListener('shopify:section:load', (event) => {
    if (!event.target) return;

    initAll(event.target);
  });
})();
